import { useCallback, useMemo } from 'react';

import { useLazyGetClientMessageInfoQuery } from '@app/api/Debug';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseCopyMessageInfoResult {
  copyMessageInfo(messageId: number): Promise<void>;
  isLoadingCopyMessageInfo: boolean;
}

export const useCopyMessageInfo = (): UseCopyMessageInfoResult => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [
    getClientMessageInfo,
    {
      isLoading: isGetClientMessageInfoLoading,
      isFetching: isGetClientMessageInfoFetching,
    },
  ] = useLazyGetClientMessageInfoQuery();

  const copyMessageInfo = useCallback(
    async (messageId: number) => {
      try {
        const fetchPromise = getClientMessageInfo({
          messageId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (typeof ClipboardItem !== 'undefined') {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': fetchPromise.then(
                (text) => new Blob([text], { type: 'text/plain' })
              ),
            }),
          ]);
        } else {
          const messageInfo = await fetchPromise;
          await navigator.clipboard.writeText(messageInfo);
        }

        showNotification(
          'Информация о сообщении скопирована в буфер',
          'success'
        );
      } catch (error) {
        showNotification(error);
      }
    },
    [activeBranchId, getClientMessageInfo, showNotification]
  );

  const isLoadingCopyMessageInfo = useMemo(
    () => isGetClientMessageInfoLoading || isGetClientMessageInfoFetching,
    [isGetClientMessageInfoLoading, isGetClientMessageInfoFetching]
  );

  return useMemo(
    () => ({
      copyMessageInfo,
      isLoadingCopyMessageInfo,
    }),
    [copyMessageInfo, isLoadingCopyMessageInfo]
  );
};
